// src/pages/courses/CoursesPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, Spinner, Badge } from "react-bootstrap";

import { db } from "../../shared/libs/firebaseConfig";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";

import "./CoursesPage.css";

// ----------------------
// helpers
// ----------------------
function toDateSafe(v) {
  try {
    if (v?.toDate) return v.toDate();
    if (v instanceof Date) return v;
    if (typeof v === "number") return new Date(v);
    if (typeof v === "string") return new Date(v);
  } catch {}
  return null;
}

const CATEGORY_OPTIONS = [
  "ทั้งหมด",
  "เนติบัณฑิต",
  "ทนายความ",
  "อัยการ",
  "ผู้พิพากษา",
  "สอบ ก.พ.",
  "ก.พ.",
];

/* function normalizeSort(s) {
  const v = String(s || "featured").toLowerCase();
  if (v === "latest") return "latest";
  if (v === "price_asc") return "price_asc";
  if (v === "price_desc") return "price_desc";
  return "featured"; // default: featured ก่อน แล้วล่าสุด
} */

function normalizeSort(s) {
  const v = String(s || "latest").toLowerCase();
  if (v === "featured") return "featured";
  if (v === "latest") return "latest";
  if (v === "price_asc") return "price_asc";
  if (v === "price_desc") return "price_desc";
  return "latest";
}

function courseHasCategory(course, category) {
  if (!category || category === "ทั้งหมด" || category === "all") return true;
  const cats = Array.isArray(course?.categories) ? course.categories : [];
  return cats.includes(category);
}

function primaryCategory(course) {
  const cats = Array.isArray(course?.categories) ? course.categories : [];
  return cats[0] || "ทั้งหมด";
}

// ----------------------
// CourseCardSimple (ตาม layout ที่คุณต้องการ)
// ----------------------
function CourseCardSimple({ c }) {
  const slugOrId = c.slug || c.id;
  const cover = c.imageCoverUrl || "https://placehold.co/1200x628/png";
  const cat = primaryCategory(c);

  const displayPrice =
    c?.courseType === "thaibar"
      ? "ราคาตามแพ็คเกจ"
      : typeof c.priceText === "string" && c.priceText.trim()
      ? `฿${c.priceText}`
      : typeof c.price === "number"
      ? `฿${c.price.toLocaleString("th-TH")}`
      : "";

  return (
    <Link to={`/courses/${slugOrId}`} className="text-decoration-none text-reset">
      <div className="card app-card h-100">
        <div className="ratio ratio-16x9 bg-surface">
          <img
            src={cover}
            alt={c.title || ""}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={(e) => (e.currentTarget.src = "https://placehold.co/1200x628/png")}
          />
        </div>

        <div className="card-body">
          <div className="fw-semibold text-main" style={{ lineHeight: 1.25 }}>
            {c.title || "-"}
          </div>

          {c.subTitle ? (
            <div
              className="text-secondary small mt-2"
              style={{
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
                overflow: "hidden",
                lineHeight: 1.35,
              }}
            >
              {c.subTitle}
            </div>
          ) : null}

          <div className="d-flex align-items-center justify-content-between mt-2 gap-2">
            <div className="fw-semibold">
              {displayPrice ? (
                <span className={c?.courseType === "thaibar" ? "text-secondary" : "text-main"}>
                  {displayPrice}
                </span>
              ) : (
                <span className="text-secondary">-</span>
              )}
            </div>

            <span className="course-card-badge">
              {cat}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function CoursesPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const sort = useMemo(() => normalizeSort(searchParams.get("sort")), [searchParams]);
  const catParam = searchParams.get("cat") || "ทั้งหมด";
  const cat = useMemo(() => (catParam && catParam !== "all" ? catParam : "ทั้งหมด"), [catParam]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const lastDocRef = useRef(null);
  const exhaustedRef = useRef(false);
  const reqIdRef = useRef(0);

  const pageSize = 18;

  const setParam = useCallback(
    (next) => {
      const cur = Object.fromEntries([...searchParams.entries()]);
      const merged = { ...cur, ...next };

      Object.keys(merged).forEach((k) => {
        if (merged[k] == null || merged[k] === "" || merged[k] === "ทั้งหมด") delete merged[k];
      });

      setSearchParams(merged, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // -------------------------
  // Firestore query builder
  // -------------------------
  const buildQuery = useCallback(
    ({ startAfterDoc = null } = {}) => {
      const ref = collection(db, "courses");
      const clauses = [where("isActive", "==", true)];

      // หมวดหมู่แบบ exact (array-contains)
      if (cat && cat !== "ทั้งหมด") {
        clauses.push(where("categories", "array-contains", cat));
      }

      // sort strategy
      if (sort === "latest") {
        clauses.push(orderBy("updatedAt", "desc"));
      } else if (sort === "price_asc") {
        clauses.push(orderBy("price", "asc"));
        clauses.push(orderBy("updatedAt", "desc"));
      } else if (sort === "price_desc") {
        clauses.push(orderBy("price", "desc"));
        clauses.push(orderBy("updatedAt", "desc"));
      } else {
        // featured ก่อน แล้วค่อยล่าสุด
        clauses.push(orderBy("isFeatured", "desc"));
        clauses.push(orderBy("updatedAt", "desc"));
      }

      clauses.push(limit(pageSize));
      if (startAfterDoc) clauses.push(startAfter(startAfterDoc));

      return query(ref, ...clauses);
    },
    [cat, sort]
  );

  const fetchFirst = useCallback(async () => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    setError("");
    exhaustedRef.current = false;
    lastDocRef.current = null;

    try {
      const q = buildQuery();
      const snap = await getDocs(q);

      if (reqIdRef.current !== myReq) return;

      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setItems(rows);

      lastDocRef.current = snap.docs[snap.docs.length - 1] || null;
      if (snap.docs.length < pageSize) exhaustedRef.current = true;
    } catch (e) {
      console.error(e);
      if (reqIdRef.current !== myReq) return;

      setError(e?.message || "โหลดคอร์สไม่สำเร็จ");
      setItems([]);
      exhaustedRef.current = true;
      lastDocRef.current = null;
    } finally {
      if (reqIdRef.current === myReq) setLoading(false);
    }
  }, [buildQuery]);

  const fetchMore = useCallback(async () => {
    if (loadingMore) return;
    if (exhaustedRef.current) return;

    const startDoc = lastDocRef.current;
    if (!startDoc) return;

    setLoadingMore(true);
    setError("");

    try {
      const q = buildQuery({ startAfterDoc: startDoc });
      const snap = await getDocs(q);

      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setItems((prev) => [...prev, ...rows]);

      lastDocRef.current = snap.docs[snap.docs.length - 1] || startDoc;
      if (snap.docs.length < pageSize) exhaustedRef.current = true;
    } catch (e) {
      console.error(e);
      setError(e?.message || "โหลดเพิ่มเติมไม่สำเร็จ");
      exhaustedRef.current = true;
    } finally {
      setLoadingMore(false);
    }
  }, [buildQuery, loadingMore]);

  useEffect(() => {
    fetchFirst();
  }, [fetchFirst]);

  // -------------------------
  // (optional) client-side featured sort fallback
  // ถ้าบาง environment ยังไม่ได้สร้าง index สำหรับ isFeatured+updatedAt
  // คุณสามารถใช้ sort=latest แทนชั่วคราว
  // -------------------------
  const visibleItems = useMemo(() => {
    // กันกรณี data บางตัวไม่มี updatedAt
    const arr = [...items];
    if (sort === "latest") {
      arr.sort((a, b) => {
        const da = toDateSafe(a.updatedAt) || toDateSafe(a.createdAt) || new Date(0);
        const dbb = toDateSafe(b.updatedAt) || toDateSafe(b.createdAt) || new Date(0);
        return dbb.getTime() - da.getTime();
      });
    }
    // cat จริง ๆ filter ที่ query แล้ว แต่กันไว้เผื่อ fallback
    return arr.filter((c) => courseHasCategory(c, cat));
  }, [items, sort, cat]);

  return (
    <div className="container py-4 courses-page">
      {/* Title + Sort */}
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-2 mb-2">
        <h3 className="mb-0">
          <strong>คอร์สทั้งหมด</strong>
        </h3>

        <div className="d-flex gap-2">
          {/* <Button
            className={sort === "featured" ? "courses-filter-btn is-active" : "courses-filter-btn"}
            size="sm"
            onClick={() => setParam({ sort: "featured" })}
          >
            แนะนำ
          </Button>
          <Button
            className={sort === "latest" ? "courses-filter-btn is-active" : "courses-filter-btn"}
            size="sm"
            onClick={() => setParam({ sort: "latest" })}
          >
            ล่าสุด
          </Button> */}
          {/* <Button
            variant={sort === "price_asc" ? "dark" : "outline-dark"}
            size="sm"
            onClick={() => setParam({ sort: "price_asc" })}
          >
            ราคาต่ำ→สูง
          </Button>
          <Button
            variant={sort === "price_desc" ? "dark" : "outline-dark"}
            size="sm"
            onClick={() => setParam({ sort: "price_desc" })}
          >
            ราคาสูง→ต่ำ
          </Button> */}
        </div>
      </div>

      {/* Category badges */}
      <div className="courses-cat-scroll mb-3">
        {CATEGORY_OPTIONS.map((c) => {
          const active = (cat || "ทั้งหมด") === c;
          return (
            <button
              key={c}
              type="button"
              className={active ? "courses-cat-badge is-active" : "courses-cat-badge"}
              onClick={() => setParam({ cat: c })}
            >
              {c}
            </button>
          );
        })}
      </div>

      {/* Loading / Error / Empty */}
      {loading ? (
        <div className="d-flex justify-content-center align-items-center my-4" style={{ minHeight: 160 }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">กำลังโหลด…</span>
          </Spinner>
        </div>
      ) : error ? (
        <div className="text-danger my-3">
          {error}
          <div className="text-secondary small mt-1">
            ถ้าเจอ error เรื่อง index ให้สร้าง composite index ตามที่ Firestore แนะนำได้เลย
          </div>
        </div>
      ) : visibleItems.length === 0 ? (
        <div className="text-secondary">ยังไม่มีคอร์สให้แสดง</div>
      ) : (
        <>
          {/* Grid */}
          <div className="row g-3 mt-2">
            {visibleItems.map((c) => (
              <div key={c.id} className="col-12 col-sm-6 col-lg-4">
                <CourseCardSimple c={c} />
              </div>
            ))}
          </div>

          {/* Load more */}
          <div className="d-flex justify-content-center my-4">
            {exhaustedRef.current ? (
              <div className="text-secondary">ไม่มีรายการเพิ่มเติม</div>
            ) : (
              <Button className="courses-loadmore-btn" onClick={fetchMore} disabled={loadingMore}>
                {loadingMore ? "กำลังโหลด..." : "โหลดเพิ่มเติม"}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}